import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import PageLayout from "../../../../../components/PageLayout";
import { usePatientEncounterReportList } from "../../../../../queries/patient-encounter/use-patient-encounter-report-list";
import { usePatientEncounterDashboardStats } from "../../../../../queries/patient-encounter/use-patient-encounter-dashboard-stats";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../utils/utils";
import PatientEncounterKPICards from "./PatientEncounterKPICards";
import PatientEncounterTable from "./PatientEncounterTable";
import { PatientEncounterFilterData } from "./patient-encounter-constants";
import { extractFilterValue, resolveDateRange } from "../report-constants";

interface PatientEncounterReportProps {
  filters: PatientEncounterFilterData;
  onFilterChange: (filters: PatientEncounterFilterData) => void;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
}

const PatientEncounterReport = ({
  filters,
  rowSelectionModel,
  onRowSelectionModelChange,
}: PatientEncounterReportProps) => {
  const providerGroupId = getXtenantId();

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const resolved = resolveDateRange(
    filters.date_range,
    filters.startDate,
    filters.endDate
  );
  const startDate = resolved.startDate;
  const endDate = resolved.endDate;

  const providerVal = extractFilterValue(filters.provider_Name);
  const locationVal = extractFilterValue(filters.location);
  const statusVal = extractFilterValue(filters.appointment_Status);
  const modeVal = extractFilterValue(filters.appointment_Mode);

  const providerUuid = providerVal ? [providerVal] : undefined;
  const locationUuid = locationVal ? [locationVal] : undefined;
  const statuses =
    statusVal && statusVal !== "ALL_STATUSES" ? [statusVal] : undefined;
  const visitTypes = modeVal && modeVal !== "ALL_MODE" ? [modeVal] : undefined;
  const searchKey = filters.search_Patient || undefined;
  const diagnosisSearch = filters.search_Diagnosis || undefined;

  const {
    data: listData,
    isLoading: listLoading,
    isRefetching: listRefetching,
  } = usePatientEncounterReportList({
    pagination,
    gridSorting,
    startDate,
    endDate,
    statuses,
    visitTypes,
    providerUuid,
    locationUuid,
    providerGroupId,
    searchKey,
    diagnosisSearch,
  });

  const { data: statsData, isLoading: statsLoading } =
    usePatientEncounterDashboardStats({
      startDate,
      endDate,
      providerUuid,
      locationUuid,
      providerGroupId,
    });

  return (
    <PageLayout
      header={
        <PatientEncounterKPICards
          data={statsData?.data}
          isLoading={statsLoading}
        />
      }
    >
      <PatientEncounterTable
        rows={listData?.data?.content ?? []}
        rowCount={listData?.data?.page?.totalElements ?? 0}
        loading={listLoading || listRefetching}
        pagination={pagination}
        onPaginationChange={setPagination}
        gridSorting={gridSorting}
        onSortChange={setGridSorting}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
      />
    </PageLayout>
  );
};

export default PatientEncounterReport;
